// src/components/feature/MemoryVerseReminder.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Mounted once in App (inside the router, after login). It:
//    1. shows a top-center popup on startup (once per day) when a verse is due,
//       or a one-time invitation when the user has no verses yet;
//    2. keeps the daily local notification scheduled (native app only);
//    3. opens /memory-verse when the notification is tapped.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, X } from 'lucide-react';
import { useMemoryVerses } from '@/hooks/feature/useScriptureMemory';
import {
  scheduleDailyReminder, cancelDailyReminder, onReminderTapped,
} from '@/lib/scriptureMemoryNotifications';

const POPUP_KEY = 'mv-startup-popup-last';
const POPUP_GAP_MS = 30 * 60 * 1000;   // show the startup popup at most once per 30 minutes

export default function MemoryVerseReminder() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ready, verses, settings, todaysVerse: dueVerse, dueCount } = useMemoryVerses();
  const todaysVerse = dueVerse || [...verses].sort((a, b) => (a.nextReview || '').localeCompare(b.nextReview || ''))[0] || null;
  const [popup, setPopup] = useState(null);          // null | 'due' | 'intro'
  const decided = useRef(false);

  // ── Startup popup: decide once per app launch ──────────────────────────────
  useEffect(() => {
    if (!ready || decided.current) return;
    decided.current = true;
    if (!settings.popupEnabled || pathname.startsWith('/memory-verse')) return;
    // Survives refreshes and Android restarts: skip if the popup was shown recently.
    try {
      const last = Number(localStorage.getItem(POPUP_KEY) || 0);
      if (Date.now() - last < POPUP_GAP_MS) return;
      localStorage.setItem(POPUP_KEY, String(Date.now()));
    } catch { /* storage unavailable */ }
    if (verses.length === 0) setPopup('intro');
    else if (dueCount > 0) setPopup('due');
    else setPopup('review');
  }, [ready, settings.popupEnabled, verses.length, dueCount, pathname]);

  // The popup stays until the user opens it or dismisses it.

  // ── Keep the daily notification in sync ────────────────────────────────────
  const body = todaysVerse
    ? `${todaysVerse.box === 0 ? 'Memorize' : 'Master'} ${todaysVerse.reference} — it only takes a couple of minutes.`
    : 'Keep your Scripture memory fresh with a quick review.';

  useEffect(() => {
    if (!ready) return;
    if (settings.reminderEnabled) {
      scheduleDailyReminder({ hour: settings.reminderHour, minute: settings.reminderMinute, body });
    } else {
      cancelDailyReminder();
    }
  }, [ready, settings.reminderEnabled, settings.reminderHour, settings.reminderMinute, body]);

  // ── Notification tap → memory verse page ───────────────────────────────────
  useEffect(() => {
    let off = () => {};
    let cancelled = false;
    onReminderTapped(() => navigate('/memory-verse')).then(unsub => {
      if (cancelled) unsub(); else off = unsub;
    });
    return () => { cancelled = true; off(); };
  }, [navigate]);

  // Returning from the background no longer re-shows the popup.

  if (!popup) return null;

  const open = () => { setPopup(null); navigate('/memory-verse'); };
  const isIntro = popup === 'intro';
  const isLearn = todaysVerse?.box === 0;
  const eyebrow = isIntro ? 'Scripture Memory' : popup === 'due' ? 'Today’s Scripture Memory' : 'Keep it fresh';
  const title = isIntro ? 'Hide God’s Word in your heart' : (todaysVerse?.reference ?? 'Scripture Memory');
  const message = isIntro
    ? 'Learn a verse in small steps and it comes back right before you would forget it.'
    : isLearn ? todaysVerse?.text : 'Say it from memory, then check how you did.';
  const cta = isIntro ? 'Pick your first verse' : isLearn ? 'Memorize this verse' : 'Master it from memory';
  const note = popup === 'due' && dueCount > 1 ? `+${dueCount - 1} more due today` : '';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-5 bg-black/50 backdrop-blur-sm"
      role="presentation"
      onClick={() => setPopup(null)}
    >
      <div
        role="dialog" aria-modal="true" aria-label={title}
        className="relative w-full max-w-sm rounded-3xl bg-white dark:bg-stone-800 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-forest-500 text-white px-6 pt-8 pb-6 text-center">
          <span className="mx-auto w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
            <BookOpen className="w-7 h-7" />
          </span>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-widest text-white/80">{eyebrow}</p>
          <p className="mt-1 font-serif font-extrabold text-2xl leading-tight">{title}</p>
        </div>
        <div className="px-6 py-5 space-y-4 text-center">
          <p className="font-serif text-base leading-relaxed text-stone-700 dark:text-stone-200 line-clamp-5">{message}</p>
          {note && <p className="text-xs font-semibold text-forest-600 dark:text-forest-300">{note}</p>}
          <div className="space-y-2">
            <button type="button" onClick={open}
              className="w-full rounded-full bg-forest-500 hover:bg-forest-600 text-white text-base font-semibold py-3 shadow-sm active:scale-95 transition">
              {cta}
            </button>
            <button type="button" onClick={() => setPopup(null)}
              className="w-full rounded-full py-2.5 text-sm font-semibold text-stone-500 dark:text-stone-400 active:scale-95 transition">
              Maybe later
            </button>
          </div>
        </div>
        <button type="button" onClick={() => setPopup(null)} aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-white/90 hover:bg-white/20 active:scale-90 transition">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}